import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ClipboardList, MessagesSquare } from "lucide-react";
import { api } from "@/lib/api";
import { ago } from "@/components/ForumComment";

const ICONS = {
  forum_reply: MessagesSquare,
  forum_accept: MessagesSquare,
  update_nudge: ClipboardList,
};

/** One bell for the whole portal — forum replies, SDTD's nudges, anything worth a look. */
export const NoticeBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);

  const read = () =>
    api
      .get("/notices")
      .then((r) => {
        setItems(r.data?.items || []);
        setUnread(r.data?.unread || 0);
      })
      .catch(() => {});

  useEffect(() => {
    read();
    const timer = setInterval(read, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) {
      api
        .post("/notices/seen")
        .then(() => setUnread(0))
        .catch(() => {});
    }
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        data-testid="notice-bell"
        title="What the portal has told you"
        onClick={toggle}
        className="relative flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-muted"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            data-testid="notice-bell-count"
            className="font-data absolute -right-1.5 -top-1.5 min-w-[18px] border border-primary bg-primary px-1 text-[10px] font-bold leading-[16px] text-primary-foreground"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notice-bell-panel"
          className="absolute right-0 top-11 z-50 w-[340px] border border-border bg-card shadow-xl"
        >
          <div className="label-caps flex items-center gap-2 border-b border-border px-4 py-3">
            <Bell className="h-3.5 w-3.5" /> What the portal has told you
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {items.map((n) => {
              const Icon = ICONS[n.kind] || Bell;
              return (
                <button
                  key={n.id}
                  type="button"
                  data-testid={`notice-${n.id}`}
                  onClick={() => {
                    setOpen(false);
                    if (n.link) navigate(n.link);
                  }}
                  className={`block w-full border-b border-border px-4 py-3 text-left transition-colors duration-200 last:border-b-0 hover:bg-muted ${
                    n.seen ? "" : "bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <Icon className="h-3 w-3" /> {n.group}
                  </div>
                  <div className="mt-1 text-sm font-medium leading-snug">{n.title}</div>
                  {n.body && (
                    <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                      {n.body}
                    </div>
                  )}
                  <div className="font-data mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {n.by ? `${n.by} · ` : ""}
                    {ago(n.at)}
                  </div>
                </button>
              );
            })}
            {!items.length && (
              <p className="px-4 py-8 text-center text-xs text-muted-foreground">
                Nothing yet. Forum replies and SDTD's nudges land here — never in your inbox alone.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
